import { StaffAccessDeniedError, type StaffAccessContext } from "./staffAccessContext"

/** Account identifiers and abort handles stay here, outside Redux. */
export function createStaffSessionRuntime(ports: {
  identity(): string | null
  read(signal: AbortSignal): Promise<StaffAccessContext>
  revoke(): void
  checking(): void
  accept(context: StaffAccessContext): void
  unavailable(): void
  startRealtime(key: string): Promise<void>
  stopRealtime(): Promise<void>
}) {
  let owner: string | null = null
  let scope: string | null = null
  let generation = 0
  let pending: { abort: AbortController; promise: Promise<void> } | null = null
  let trailing = false
  let stopping = Promise.resolve()

  function clear(nextOwner: string | null) {
    generation++
    owner = nextOwner
    scope = null
    pending?.abort.abort()
    pending = null
    trailing = false
    ports.revoke()
    stopping = stopping.then(() => ports.stopRealtime())
    void stopping.catch(() => {})
  }
  const current = (id: string, epoch: number) => owner === id && ports.identity() === id && generation === epoch

  function read(): Promise<void> {
    if (pending) return pending.promise
    if (!owner) return Promise.resolve()
    const id = owner, epoch = generation
    const abort = new AbortController()
    ports.checking()
    // Defer execution until pending is installed, including synchronous test transports.
    const promise = Promise.resolve().then(async () => {
      try {
        await stopping
        if (!current(id, epoch)) return
        const context = await ports.read(abort.signal)
        if (!current(id, epoch)) return
        if (scope !== null && scope !== context.scopeKey) {
          scope = null
          ports.revoke()
          stopping = stopping.then(() => ports.stopRealtime())
          await stopping
          if (!current(id, epoch)) return
        }
        scope = context.scopeKey
        ports.accept(context)
        // Realtime failure does not negate a fresh authorized HTTP context.
        void ports.startRealtime(`${id}/${scope}/${epoch}`).catch(() => {})
      } catch (error) {
        if (!current(id, epoch)) return
        if (error instanceof StaffAccessDeniedError) clear(id)
        // A network outage is not proof of lost permission: retain permitted
        // drafts, but the UI/transport must make them read-only until checked.
        ports.unavailable()
      }
    }).finally(() => {
      if (pending?.abort !== abort) return
      pending = null
      if (trailing && current(id, epoch)) { trailing = false; void read() }
    })
    pending = { abort, promise }
    return promise
  }

  return {
    ensure(): Promise<void> {
      const identity = ports.identity()
      if (identity !== owner) clear(identity)
      return scope || !owner ? Promise.resolve() : read()
    },
    refresh(): Promise<void> {
      const identity = ports.identity()
      if (identity !== owner) clear(identity)
      if (pending) { trailing = true; return pending.promise }
      return read()
    },
    /** A hint blocks writes; only a checked access change/denial discards the draft. */
    accessHint(): Promise<void> {
      const identity = ports.identity()
      if (identity !== owner) clear(identity)
      ports.unavailable()
      if (pending) { trailing = true; return pending.promise }
      return read()
    },
    isCurrent(identity: string | null): boolean { return !!identity && identity === owner && identity === ports.identity() && scope !== null },
    dispose(): Promise<void> { clear(null); return stopping },
  }
}
